import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import React, { useState } from 'react';
import { NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import styled, { css } from 'styled-components';
import config from 'utils/config';
import isAdmin from 'utils/session/isAdmin';
import { pxToRem } from 'utils/ui/units';
import { COLLAPSED_NAVBAR_HEIGHT, spacing, breakpoints, maxPageWidth, z } from 'styles/globals';
import useBreakpoints from 'hooks/useBreakpoints';
import useIsElementInView from 'hooks/useIsElementInView';
import ThemeSection from 'components/ThemeSection';
import Logo from 'components/Logo';
import NavBarTab from 'components/Tab/NavBarTab';
import Avatar from 'components/Avatar';
import CoachAiAvatar from 'components/Avatar/CoachAIAvatar';
import { useFetchMindsetAgent, useFetchUserProfile } from 'actions/userProfile';
import Flex, { Box } from 'components/Flex';
import Button from 'components/Button';
import { COACH_AI_ROUTE } from '../../pages/coach-ai';
import NewUserMenu from './userMenu/NewUserMenu';
import getMyTeamsLink from './userMenu/getMyTeamsLink';
import MainSearch from './NewMainSearch';
import { getCustomNavbarLink } from './getNavigationLinks';
import AdminShortcuts from './AdminShortcuts';
export var NAV_BREAKPOINTS = {
  small: 'xxs',
  medium: 'm'
};
export var NewPageWrapper = styled.div.withConfig({
  displayName: "NewNavigation__NewPageWrapper",
  componentId: "sc-13q230f-0"
})(["display:flex;flex-direction:column;min-height:100vh;"]);
var Header = styled.header.withConfig({
  displayName: "NewNavigation__Header",
  componentId: "sc-13q230f-1"
})(["position:sticky;top:0;z-index:", ";color:", ";background-color:", ";@media (max-width:", "){transition:transform 200ms ease-in;will-change:transform;&[data-is-sticky='true']{transform:translateY(-50%);transition-timing-function:ease-out;}}", ""], z.navbars, function (p) {
  return p.theme.text;
}, function (p) {
  return p.theme.background;
}, breakpoints[NAV_BREAKPOINTS.medium], function (p) {
  return p.theme.globals.mainNavShadow && css(["box-shadow:", ";"], p.theme.globals.mainNavShadow);
});
var HeaderContent = styled.div.withConfig({
  displayName: "NewNavigation__HeaderContent",
  componentId: "sc-13q230f-2"
})(["position:relative;display:grid;align-items:center;grid-template-columns:fit-content(", ") 1fr min-content;grid-template-rows:repeat(2,", ");grid-template-areas:'logo search dropdown' 'menu menu menu';width:100%;max-width:", ";margin:0 auto;padding:0 ", ";@media (min-width:", "){padding:0 ", ";column-gap:", ";}@media (min-width:", "){height:", ";grid-template-columns:fit-content(", ") minmax(", ",", ") auto min-content;grid-template-areas:'logo search menu dropdown';padding:0 ", ";}"], pxToRem(200), pxToRem(COLLAPSED_NAVBAR_HEIGHT), pxToRem(maxPageWidth), spacing.xs, breakpoints[NAV_BREAKPOINTS.small], spacing.s, spacing.l, breakpoints[NAV_BREAKPOINTS.medium], pxToRem(COLLAPSED_NAVBAR_HEIGHT), pxToRem(200), pxToRem(100), pxToRem(450), spacing.l);
var LogoWrapper = styled.div.withConfig({
  displayName: "NewNavigation__LogoWrapper",
  componentId: "sc-13q230f-3"
})(["height:100%;grid-area:logo;"]);
var SearchWrapper = styled.div.withConfig({
  displayName: "NewNavigation__SearchWrapper",
  componentId: "sc-13q230f-4"
})(["grid-area:search;@media (width < ", "){display:none;[data-has-mobile-search='true'] &{display:block;grid-area:menu;}}"], breakpoints[NAV_BREAKPOINTS.small]);
var MobileSearchTriggerWrapper = styled.div.withConfig({
  displayName: "NewNavigation__MobileSearchTriggerWrapper",
  componentId: "sc-13q230f-5"
})(["display:flex;justify-self:end;@media (min-width:", "){display:none;}"], breakpoints[NAV_BREAKPOINTS.small]);
var MainNavWrapper = styled.nav.withConfig({
  displayName: "NewNavigation__MainNavWrapper",
  componentId: "sc-13q230f-6"
})(["grid-area:menu;[data-has-mobile-search='true'] &{display:none;@media (min-width:", "){display:block;}}@media (min-width:", "){justify-self:end;}"], breakpoints[NAV_BREAKPOINTS.small], breakpoints[NAV_BREAKPOINTS.medium]);
var SecondaryNavWrapper = styled.nav.withConfig({
  displayName: "NewNavigation__SecondaryNavWrapper",
  componentId: "sc-13q230f-7"
})(["grid-area:dropdown;@media (min-width:", "){margin-inline:-", ";}"], breakpoints[NAV_BREAKPOINTS.medium], spacing.m);
var List = styled.ul.withConfig({
  displayName: "NewNavigation__List",
  componentId: "sc-13q230f-8"
})(["display:flex;width:100%;@media (min-width:", "){gap:", ";}"], breakpoints[NAV_BREAKPOINTS.medium], spacing.s);
var ListItem = styled.li.withConfig({
  displayName: "NewNavigation__ListItem",
  componentId: "sc-13q230f-9"
})(["display:flex;align-self:stretch;flex:", " 1 0%;@media (min-width:", "){flex-basis:auto;}"], function (_ref) {
  var _ref$grow = _ref.grow,
    grow = _ref$grow === void 0 ? true : _ref$grow;
  return grow ? 1 : 0;
}, breakpoints[NAV_BREAKPOINTS.medium]);
function MainNavLinkTab(_ref2) {
  var children = _ref2.children,
    to = _ref2.to,
    icon = _ref2.icon;
  return /*#__PURE__*/React.createElement(NavBarTab, {
    as: NavLink,
    to: to,
    icon: icon,
    activeClassName: "is-active",
    height: COLLAPSED_NAVBAR_HEIGHT
  }, children);
}
function NewNavigation() {
  var _useTranslation = useTranslation(),
    t = _useTranslation.t;
  var _useFetchUserProfile = useFetchUserProfile(),
    userProfile = _useFetchUserProfile.userProfile;
  var _useBreakpoints = useBreakpoints(),
    breakpointXXS = _useBreakpoints.xxs,
    breakpointS = _useBreakpoints.s,
    breakpointL = _useBreakpoints.l;
  var customNavbarLink = getCustomNavbarLink();
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    isMobileSearchOpen = _useState2[0],
    setIsMobileSearchOpen = _useState2[1];
  function toggleMobileSearch() {
    setIsMobileSearchOpen(function (prev) {
      return !prev;
    });
  }
  var _useIsElementInView = useIsElementInView({
      initialValue: true
    }),
    isInView = _useIsElementInView.isInView,
    watchedElementRef = _useIsElementInView.watchedElementRef;
  var wasPageScrolled = !isInView;
  var userIsAdmin = isAdmin();
  var myTeamsLink = getMyTeamsLink();
  var hasAdminShortcuts = breakpointL && (userIsAdmin || Boolean(myTeamsLink));
  var _useFetchMindsetAgent = useFetchMindsetAgent(),
    hasMindsetAgent = _useFetchMindsetAgent.hasMindsetAgent;
  return /*#__PURE__*/React.createElement(ThemeSection, {
    name: "navbar"
  }, !breakpointS && /*#__PURE__*/React.createElement(Box, {
    ref: watchedElementRef,
    position: "absolute",
    top: 10,
    width: "1px",
    height: "1px"
  }), /*#__PURE__*/React.createElement(Header, {
    "data-has-mobile-search": isMobileSearchOpen,
    "data-is-sticky": wasPageScrolled
  }, /*#__PURE__*/React.createElement(HeaderContent, null, /*#__PURE__*/React.createElement(LogoWrapper, null, /*#__PURE__*/React.createElement(Logo, {
    id: "topbar_logo",
    link: "/home",
    alt: t('hub_title')
  })), /*#__PURE__*/React.createElement(MobileSearchTriggerWrapper, null, /*#__PURE__*/React.createElement(Button, {
    iconOnly: true,
    icon: isMobileSearchOpen ? 'menu' : 'search',
    iconScale: 1.5,
    variant: "transparent",
    onClick: toggleMobileSearch,
    mr: "-xs"
  }, isMobileSearchOpen ? t('actions_close_search') : t('actions_search')), hasMindsetAgent && /*#__PURE__*/React.createElement(Button, {
    iconOnly: true,
    icon: /*#__PURE__*/React.createElement(CoachAiAvatar, null),
    variant: "transparent",
    as: NavLink,
    to: COACH_AI_ROUTE,
    mr: "-xs"
  }, t('coach_ai_ask', {
    coachAiName: config.coachAiName
  }))), (breakpointXXS || isMobileSearchOpen) && /*#__PURE__*/React.createElement(SearchWrapper, null, /*#__PURE__*/React.createElement(MainSearch, {
    hasMindsetAgent: hasMindsetAgent
  })), /*#__PURE__*/React.createElement(MainNavWrapper, {
    "aria-label": t('main_navigation')
  }, /*#__PURE__*/React.createElement(List, null, /*#__PURE__*/React.createElement(ListItem, null, /*#__PURE__*/React.createElement(MainNavLinkTab, {
    to: "/home",
    icon: breakpointS ? undefined : 'home'
  }, t('navbar_home'))), /*#__PURE__*/React.createElement(ListItem, null, /*#__PURE__*/React.createElement(MainNavLinkTab, {
    to: "/browse",
    icon: breakpointS ? undefined : 'topic'
  }, t('navbar_browse'))), /*#__PURE__*/React.createElement(ListItem, null, /*#__PURE__*/React.createElement(MainNavLinkTab, {
    to: "/profile",
    icon: breakpointS ? undefined : 'user'
  }, /*#__PURE__*/React.createElement(Flex, {
    gap: "xxs"
  }, t('navbar_profile'), breakpointS && (userProfile === null || userProfile === void 0 ? void 0 : userProfile.pictureUrl) && /*#__PURE__*/React.createElement(Avatar, {
    shady: true,
    pictureUrl: userProfile === null || userProfile === void 0 ? void 0 : userProfile.pictureUrl
  })))), customNavbarLink.enabled && /*#__PURE__*/React.createElement(ListItem, null, /*#__PURE__*/React.createElement(NavBarTab, {
    allowTextWrap: true,
    as: "a",
    href: customNavbarLink.url,
    icon: breakpointS ? undefined : 'external',
    height: COLLAPSED_NAVBAR_HEIGHT
  }, customNavbarLink.title)), hasAdminShortcuts && /*#__PURE__*/React.createElement(ListItem, null, /*#__PURE__*/React.createElement(AdminShortcuts, {
    isAdmin: userIsAdmin,
    myTeamsLink: myTeamsLink
  })))), /*#__PURE__*/React.createElement(SecondaryNavWrapper, {
    "aria-label": t('secondary_navigation')
  }, /*#__PURE__*/React.createElement(NewUserMenu, null)))));
}
export default NewNavigation;